/**
 * Industries Stats Component
 * 
 * This component displays statistics about the company's industry impact
 * with a minimal, space-efficient design following the design system patterns.
 */
'use client';

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Badge, Card, Text as TextComponent, Heading as HeadingComponent } from '@/components/ui/design-system';
import { cn } from '@/lib/utils';

const MotionBadge = motion(Badge);

// Icons moved to a separate object for better organization
const StatIcons = {
  ThumbsUp: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 10v12"></path>
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path>
    </svg>
  ),
  BarChart: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10"></line>
      <line x1="18" y1="20" x2="18" y2="4"></line>
      <line x1="6" y1="20" x2="6" y2="16"></line>
    </svg>
  ),
  Users: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  Award: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="7"></circle>
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
    </svg>
  )
};

// Text component wrapper to fix the type error
const Text = ({ children, ...props }: React.ComponentProps<typeof TextComponent> & { children: React.ReactNode }) => (
  <TextComponent {...props}>{children}</TextComponent>
);

// Heading component wrapper to fix the type error
const Heading = ({ children, ...props }: React.ComponentProps<typeof HeadingComponent> & { children: React.ReactNode }) => (
  <HeadingComponent {...props}>{children}</HeadingComponent>
);

// Stat card component for better encapsulation and reusability
const StatCard = ({ 
  icon, 
  value, 
  label, 
  description, 
  color, 
  index 
}: { 
  icon: React.ReactNode;
  value: string;
  label: string;
  description: string;
  color: string;
  index: number;
}) => {
  // Regex to separate leading numbers from the rest of the string
  const valueParts = value.match(/^(\d+)(.*)/);
  
  const numericValue = valueParts ? valueParts[1] : value;
  const suffix = valueParts ? valueParts[2] : '';

  return (
    <Card 
      className={cn(
        "p-4 text-center transition-all duration-300 hover:translate-y-[-3px] border border-gray-100 dark:border-gray-800",
        "rounded-xl animate-fade-in",
        `animation-delay-${(index + 1) * 100}`
      )}
    >
      <div className={cn(
        "mb-3 inline-flex items-center justify-center w-10 h-10 rounded-lg",
        color
      )}>
        {icon}
      </div>
      
      <div className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">
        <span className="premium-gradient-text">{numericValue}</span>{suffix}
      </div>
      
      <TextComponent variant="body" className="text-gray-700 dark:text-gray-300 text-sm font-medium">
        {label}
      </TextComponent>
      
      <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
        <TextComponent variant="body" className="text-xs text-gray-500 dark:text-gray-400">
          {description}
        </TextComponent>
      </div>
    </Card>
  );
};

export default function IndustriesStats() {
  // Using useMemo to prevent recreating the stats array on each render
  const stats = useMemo(() => [
    {
      icon: <StatIcons.ThumbsUp />,
      value: "95%",
      label: "Client Retention Rate",
      description: "Year after year customer loyalty",
      color: "bg-primary-500/10 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400"
    },
    {
      icon: <StatIcons.Users />,
      value: "8+",
      label: "Industries Served",
      description: "Diverse clientele across sectors",
      color: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
    },
    {
      icon: <StatIcons.BarChart />,
      value: "10L+",
      label: "Products Delivered",
      description: "And growing every day",
      color: "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
    },
    {
      icon: <StatIcons.Award />,
      value: "9",
      label: "Years of Excellence",
      description: "Established in 2015",
      color: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
    }
  ], []);

  return (
    <section className="py-10 sm:py-14 relative overflow-hidden bg-white dark:bg-gray-900/80">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.02] dark:opacity-[0.03] mix-blend-overlay pointer-events-none transform-gpu"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-1 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-4 md:px-6 lg:px-8">
        <div className="text-center mb-6 animate-fade-in">
          <div className="mb-4">
            <MotionBadge
              variant="premium"
              size="lg"
              radius="full"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              Our Impact
            </MotionBadge>
          </div>
          <Heading level={2} className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 dark:text-white">
          Industry Leaders in Premium Packaging
          </Heading>          
          <Text variant="body" className="text-gray-600 dark:text-gray-300 text-lg max-w-3xl mx-auto">
            We've established ourselves as a trusted partner across multiple industries.
          </Text>          
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-6xl mx-auto">
          {stats.map((stat, index) => (
            <StatCard
              key={stat.label}
              icon={stat.icon}
              value={stat.value}
              label={stat.label}
              description={stat.description}
              color={stat.color}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
} 